import type { SubscriptionData } from "@repo/dashboard-core";

import type { RecentEsim } from "@/lib/types/esim";
import type { TopUp } from "@/lib/types/topup";

/** BO `Product.slug` of the eSIM subscription (GD-98). */
export const ESIM_PRODUCT_SLUG = "esim";

/** Cancelled but still inside its paid or trial period, so still a member. */
const RUNNING_STATUSES = new Set(["active", "pending_cancellation"]);

type SubscriptionWithPlan = SubscriptionData & {
  subscriptionPlan?: { product?: { slug?: string } | null }[];
};

/**
 * Whether this customer's subscription is the eSIM one (GD-98). It only
 * settles the home for a customer who has bought nothing yet — see `isEsimHome`.
 *
 * `/members/subscriptions` returns each subscription with its default plan and
 * that plan's product, so the product is what tells an eSIM customer apart —
 * the domain cannot, one account spans every brand. Every subscription has to
 * be an eSIM one: a customer who also holds a top-up plan keeps the top-up
 * home, which is the ticket's "TU's MZ unaffected".
 *
 * Only running subscriptions count (GD-171): the API sends every subscription
 * the account ever held, on every brand, so one expired plan from years ago
 * kept a returning customer who had just bought an eSIM on the top-up home.
 */
export function isEsimCustomer(
  subscriptions: SubscriptionData[] | undefined,
): boolean {
  const running = subscriptions?.filter(({ status }) =>
    RUNNING_STATUSES.has(status),
  );
  if (!running?.length) return false;

  return running.every((subscription) =>
    (subscription as SubscriptionWithPlan).subscriptionPlan?.some(
      // The landing checkouts sell their own eSIM products, named like every
      // other one (`tu_subscription_esim_1m_49_fly`): the `esim` word in the
      // slug is what they share with BO's `esim` (GD-171).
      // ponytail: slug naming convention, switch to the application type if
      // `/members/subscriptions` ever sends it
      (plan) => !!plan.product?.slug?.split("_").includes(ESIM_PRODUCT_SLUG),
    ),
  );
}

/**
 * Whether the MemberZone home is the eSIM one (GD-135).
 *
 * eSIM sells the same membership as Top Up, so the subscription alone left a
 * customer who only bought eSIMs on a home worded for top-ups. What they bought
 * decides instead: only eSIMs is the eSIM home, only top-ups the top-up one,
 * and with both, whichever was bought last.
 */
export function isEsimHome({
  subscriptions,
  topUps,
  esims,
}: {
  subscriptions: SubscriptionData[] | undefined;
  topUps: Pick<TopUp, "createdAt">[];
  esims: Pick<RecentEsim, "purchasedAt">[];
}): boolean {
  if (!topUps.length && !esims.length) return isEsimCustomer(subscriptions);
  if (!topUps.length) return true;
  if (!esims.length) return false;

  return (
    latestTime(esims.map((esim) => esim.purchasedAt)) >
    latestTime(topUps.map((topUp) => topUp.createdAt))
  );
}

function latestTime(dates: string[]): number {
  return Math.max(...dates.map((date) => new Date(date).getTime() || 0));
}
